import type {
  Academy,
  AcademyCertification,
  AcademyCollection,
  AcademyCollectionContent,
  AcademyEvent,
} from '~/generated_types';
import type { RecommendedContentsForExternalUserData } from '../../api';
import { Col, Link, Row, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { ObjectType } from '~/features/learner-pages';
import { BaseThemeText } from '~/clc/features/theming/learner/components';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { useGetAcademyRecommendedContentsForExternalUser, useGetAcademyExternalUserContentInfo } from '../../api';
import {
  StyledCarousel,
  StyledCustomCard,
  StyledIconLeftButton,
  StyledIconRightButton,
  StyledSection,
  StyledSectionInnerContainer,
} from './AcademyLearnerRecommendedContentsSection.styles';

const { ASSET_PICKER_TYPES } = window.APP_CONSTANTS;

export const AcademyLearnerRecommendedContentsSection = ({
  academy,
  inAdminPreview = false,
  academyRecommendedContents = [],
}: {
  academy: Academy['MINIMUM_THEME_CONTENT'];
  inAdminPreview?: boolean;
  academyRecommendedContents?: RecommendedContentsForExternalUserData[];
}) => {
  const TRANSLATED_TITLE = I18n.t('common.academy.recommended_content.title');
  const TRANSLATED_VIEW_ALL = I18n.t('common.academy.recommended_content.view_all');

  const theme = useTheme();
  const sectionEnabled = academy.recommendedContentSectionEnabled;

  const { data: recommendedData = [], isLoading: isLoadingRecommended } =
    useGetAcademyRecommendedContentsForExternalUser(
      {
        academyId: academy.id,
      },
      {
        enabled: sectionEnabled,
        initialData: inAdminPreview ? academyRecommendedContents : undefined,
      },
    );
  const { data: externalUserInfo, isLoading: isLoadingExternalInfo } = useGetAcademyExternalUserContentInfo(
    {
      academyId: academy.id,
    },
    {
      enabled: sectionEnabled,
      initialData: inAdminPreview
        ? {
            accessibleContentIds: [],
            userAssignments: [],
            certificationUsers: [],
          }
        : undefined,
    },
  );

  if (
    isLoadingRecommended ||
    isLoadingExternalInfo ||
    !sectionEnabled ||
    !isFeatureEnabled('fy26_q2_clc_recommendations')
  )
    return null;

  const contents = recommendedData
    .map(({ recommendable: content }) => {
      switch (content.type) {
        case ASSET_PICKER_TYPES.ACADEMY_EVENT: {
          const event = content as AcademyEvent['LEARNER_CARD_CONTENT'];

          return {
            id: event.id,
            objectId: event.id,
            objectType: ObjectType.event,
            learnerObject: event,
          };
        }
        case ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION: {
          const certification = content as AcademyCertification['LEARNER_CARD_CONTENT'];
          const academyCertificationUser = externalUserInfo.certificationUsers?.find(
            (certUser) => certUser.academyCertificationId === certification.id,
          );

          if (academyCertificationUser?.awarded) return null;

          return {
            id: certification.id,
            objectId: certification.id,
            objectType: ObjectType.certification,
            learnerObject: {
              ...certification,
              academyCertificationUser,
            },
          };
        }
        case ASSET_PICKER_TYPES.ACADEMY_COLLECTION_CONTENT: {
          const collectionContent = content as AcademyCollectionContent['LEARNER_CARD_CONTENT'];
          const assignment = externalUserInfo.userAssignments?.find(
            (assn) => assn.contentId === collectionContent.contentId,
          );

          if (assignment?.isCompleted) return null;

          return {
            id: collectionContent.id,
            objectId: collectionContent.id,
            objectType: collectionContent.contentType as ObjectType,
            learnerObject: {
              ...collectionContent,
              assignment,
              ...collectionContent.content,
            },
          };
        }
        case ASSET_PICKER_TYPES.ACADEMY_COLLECTION: {
          const collection = content as AcademyCollection['LEARNER_CARD_CONTENT'];
          return {
            id: collection.id,
            objectId: collection.id,
            objectType: ObjectType.collection,
            learnerObject: {
              ...collection,
            },
          };
        }
        default:
          return null;
      }
    })
    .filter((content) => content !== null);

  const renderRecommendedContents = () =>
    contents.map((content) => (
      <StyledCustomCard
        key={content.id}
        pageObject={content}
        academy={academy}
        accessibleContentIds={externalUserInfo.accessibleContentIds || []}
        pageSlug={undefined}
        urlParams={{
          from_catalog: 'true',
        }}
      />
    ));

  return contents.length > 0 ? (
    <StyledSection $backgroundColor={academy.themeOptions.recommendationsBackgroundColor}>
      <StyledSectionInnerContainer>
        <Stack size={6}>
          <Row align="flex-end" justify="space-between">
            <Col>
              <BaseThemeText.Heading level="md" themeTextColor={academy.themeOptions.recommendationsTitleColor}>
                {academy.recommendedContentSectionTitle || TRANSLATED_TITLE}
              </BaseThemeText.Heading>
            </Col>
            {academy.catalogPageEnabled && (
              <Col xs="auto">
                <Link href="/catalog">
                  <BaseThemeText.Span
                    themeTextColor={
                      academy.themeOptions.recommendationsLinkColor || academy.themeOptions.breadCrumbLinkColor
                    }
                  >
                    {TRANSLATED_VIEW_ALL}
                  </BaseThemeText.Span>
                </Link>
              </Col>
            )}
          </Row>
          <StyledCarousel
            height="auto"
            isCentered={false}
            showButtonControls
            arrowLeftEl={
              <StyledIconLeftButton
                iconName="arrow-left:regular"
                label="Previous"
                variant={academy.themeOptions.recommendedationsCarouselArrowStyle === 'accent' ? 'solid' : 'outline'}
                themeColor={
                  academy.themeOptions.recommendedationsCarouselArrowStyle === 'accent'
                    ? academy.themeOptions.accentColor
                    : theme.colors.on[600]
                }
              />
            }
            arrowRightEl={
              <StyledIconRightButton
                iconName="arrow-right:regular"
                label="Next"
                variant={academy.themeOptions.recommendedationsCarouselArrowStyle === 'accent' ? 'solid' : 'outline'}
                themeColor={
                  academy.themeOptions.recommendedationsCarouselArrowStyle === 'accent'
                    ? academy.themeOptions.accentColor
                    : theme.colors.on[600]
                }
              />
            }
            showPaginationControls={false}
            pageSizeByBreakpoint={{
              xs: 2,
              md: 3,
              lg: 3,
              xl: 3,
            }}
          >
            {renderRecommendedContents()}
          </StyledCarousel>
        </Stack>
      </StyledSectionInnerContainer>
    </StyledSection>
  ) : null;
};
